import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { FiChevronDown, FiX } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { useAuthStore } from '../../store/authStore'
import { soleOptionIdFromList, useAutoSelectSingleOption } from '../../hooks/useAutoSelectSingleOption'
import { isSameEntityId } from '../../utils/entityId'
import DropdownSearchHeader from './DropdownSearchHeader'
import { useLookupRefresh } from '../../hooks/useLookupRefresh'
import { useDropdownFieldConfig } from '../../hooks/useDropdownFieldConfig'
import { formatConfiguredLabel } from '../../utils/dropdownFieldConfig'
import { mapPagedResponse, useInfiniteLookup } from '../../hooks/useInfiniteLookup'

interface Branch {
  id: string
  name: string
  isActive: boolean
}

function mapBranchRow(row: any): Branch | null {
  const id = String(row?.id ?? row?.ID ?? '').trim()
  const name = String(row?.name ?? row?.Name ?? '').trim()
  if (!id || !name) return null
  const isActiveValue = row?.isActive ?? row?.IsActive ?? true
  const isActive = isActiveValue === true || isActiveValue === 1 || isActiveValue === 'true'
  if (!isActive) return null
  return { id, name, isActive }
}

interface BranchDropdownProps {
  value?: number | string
  onChange: (value: number | string | undefined) => void
  placeholder?: string
  className?: string
  error?: string
  disabled?: boolean
  autoSelectSingle?: boolean
}

export default function BranchDropdown({
  value,
  onChange,
  placeholder,
  className = '',
  error,
  disabled = false,
  autoSelectSingle = true,
}: BranchDropdownProps) {
  const { language } = useLanguageStore()
  const user = useAuthStore((s) => s.user)
  const isRTL = language === 'ar'
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const fieldConfig = useDropdownFieldConfig('branch')
  const getId = useCallback((b: Branch) => String(b.id || ''), [])

  const {
    items: branches,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
    sentinelRef,
  } = useInfiniteLookup<Branch>({
    queryKey: ['branches', 'dropdown', user?.companyId ?? ''],
    enabled: !disabled && (isOpen || !value || autoSelectSingle),
    pageSize: 40,
    searchTerm,
    searchFields: fieldConfig.searchFields,
    getId,
    scrollRootRef: listRef,
    fetchPage: async ({ page, pageSize, search, searchFields }) => {
      const params: Record<string, string | number> = { page, pageSize }
      if (user?.companyId) params.companyId = user.companyId
      if (search) params.searchTerm = search
      if (searchFields.length > 0) params.searchFields = searchFields.join(',')
      const response = await apiClient.get(API_ENDPOINTS.branches.list, { params })
      return mapPagedResponse(response.data, mapBranchRow, page, pageSize)
    },
  })

  const { data: selectedById } = useQuery({
    queryKey: ['branches', 'selected', value],
    enabled: !!value && !branches.some((b) => isSameEntityId(b.id, value)),
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.branches.get(String(value)))
      return mapBranchRow(response.data)
    },
  })

  const filteredBranches = useMemo(() => {
    if (!selectedById?.id) return branches
    if (branches.some((b) => isSameEntityId(b.id, selectedById.id))) return branches
    return [selectedById, ...branches]
  }, [branches, selectedById])

  const filteredBranchesRef = useRef(filteredBranches)
  useEffect(() => {
    filteredBranchesRef.current = filteredBranches
  }, [filteredBranches])

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchTerm])

  const soleBranchIdStr = soleOptionIdFromList(branches, (b) => b.id)

  const applySoleBranch = useCallback(() => {
    const only = branches[0]
    if (!only?.id) return
    const branchIdStr = String(only.id).trim()
    if (!branchIdStr) return
    onChange(branchIdStr)
  }, [branches, onChange])

  const { markUserCleared } = useAutoSelectSingleOption({
    soleIdStr: soleBranchIdStr,
    currentValue: value,
    onApplySole: applySoleBranch,
    isLoading,
    disabled,
    autoSelectSingle,
  })

  const { onRefresh, isRefreshing } = useLookupRefresh({
    refetch,
    isFetching,
    items: branches,
    selectedId: value,
    onClearSelection: () => onChange(undefined),
    language,
  })

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current?.contains(target) || portalRef.current?.contains(target)) return
      setIsOpen(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
    }

    const timeoutId = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 100)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = useCallback(
    (branchId: string) => {
      const id = branchId.trim()
      onChange(id || undefined)
      setIsOpen(false)
      setSearchTerm('')
      setHighlightedIndex(-1)
    },
    [onChange]
  )

  const handleSelectRef = useRef(handleSelect)
  useEffect(() => {
    handleSelectRef.current = handleSelect
  }, [handleSelect])

  const highlightedIndexRef = useRef(highlightedIndex)
  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex
  }, [highlightedIndex])

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1)
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const t = event.target as Node | null
      if (t && !dropdownRef.current?.contains(t) && !portalRef.current?.contains(t)) {
        return
      }

      const list = filteredBranchesRef.current
      const totalItems = list.length

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          if (totalItems === 0) return
          setHighlightedIndex((prev) => {
            const newIndex = prev < totalItems - 1 ? prev + 1 : prev
            requestAnimationFrame(() =>
              itemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            )
            return newIndex
          })
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          if (totalItems === 0) return
          setHighlightedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : 0
            requestAnimationFrame(() =>
              itemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            )
            return newIndex
          })
          break
        }
        case 'Enter': {
          event.preventDefault()
          const idx = highlightedIndexRef.current
          if (idx >= 0 && idx < totalItems) {
            const b = list[idx]
            if (b?.id) handleSelectRef.current(String(b.id))
          }
          break
        }
        case 'Escape': {
          event.preventDefault()
          setIsOpen(false)
          setSearchTerm('')
          setHighlightedIndex(-1)
          break
        }
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const selectedBranch =
    filteredBranches.find((branch: Branch) => isSameEntityId(branch.id, value)) ||
    selectedById ||
    undefined

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    markUserCleared()
    onChange(undefined)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) setIsOpen((prev) => !prev)
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        className={`input-field w-full ${disabled ? 'cursor-not-allowed opacity-50 bg-gray-100' : 'cursor-pointer'} flex items-center justify-between ${
          error ? 'border-red-500' : ''
        } ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
      >
        <span className={`flex-1 truncate ${isRTL ? 'text-right' : 'text-left'} ${!value ? 'text-gray-400' : 'text-gray-800'}`}>
          {selectedBranch
            ? formatConfiguredLabel(selectedBranch, 'branch', fieldConfig.triggerFields, language)
            : placeholder || (language === 'ar' ? 'اختر الفرع' : 'Select Branch')}
        </span>
        <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`} style={{ minWidth: '24px' }}>
          {value !== undefined && value !== null && value !== '' && !disabled && (
            <div onClick={handleClear} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 cursor-pointer" role="button">
              <FiX className="w-4 h-4" />
            </div>
          )}
          <FiChevronDown className={`text-gray-400 transition-transform w-5 h-5 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {error && <p className={`mt-1 text-sm text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>{error}</p>}

      {!disabled &&
        isOpen &&
        dropdownRef.current &&
        createPortal(
          <div
            ref={portalRef}
            className={`fixed z-[99999] bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-hidden ${isRTL ? 'text-right' : 'text-left'}`}
            style={{
              zIndex: 99999,
              width: dropdownRef.current.offsetWidth || 'auto',
              top: `${dropdownRef.current.getBoundingClientRect().bottom + 4}px`,
              left: isRTL ? 'auto' : `${dropdownRef.current.getBoundingClientRect().left}px`,
              right: isRTL
                ? `${window.innerWidth - dropdownRef.current.getBoundingClientRect().right}px`
                : 'auto',
            }}
          >
            <DropdownSearchHeader
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onRefresh={onRefresh}
              isRefreshing={isRefreshing}
              isRTL={isRTL}
              placeholder={language === 'ar' ? 'بحث بالفرع...' : 'Search branch...'}
              className="p-2 border-b border-gray-200 shrink-0"
            />

            <div ref={listRef} className="overflow-y-auto max-h-48">
              {isLoading ? (
                <div className={`p-4 text-center text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                </div>
              ) : filteredBranches.length === 0 ? (
                <div className={`p-4 text-center text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {language === 'ar' ? 'لا توجد نتائج مطابقة' : 'No matching results'}
                </div>
              ) : (
                <ul className="py-1">
                  {filteredBranches.map((branch: Branch, index: number) => {
                    const isSelected = isSameEntityId(branch.id, value)
                    const isHighlighted = highlightedIndex === index
                    return (
                      <li key={String(branch.id)}>
                        <button
                          type="button"
                          ref={(el) => {
                            itemRefs.current[index] = el
                          }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleSelect(String(branch.id))
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          className={`w-full px-4 py-2.5 text-sm transition-colors ${isRTL ? 'text-right' : 'text-left'} ${
                            isHighlighted
                              ? 'bg-primary-100 text-gray-900'
                              : isSelected
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {formatConfiguredLabel(branch, 'branch', fieldConfig.listFields, language)}
                        </button>
                      </li>
                    )
                  })}
                  <div ref={sentinelRef} className="h-1" />
                  {isFetchingNextPage && (
                    <div className="p-2 text-center text-xs text-gray-400">
                      {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                    </div>
                  )}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
